import { ApolloError, PureQueryOptions, useQuery } from '@apollo/client';
import { faDownload, faTrash, faUpload } from '@fortawesome/pro-regular-svg-icons';
import { Button, LinkButton, Multiselect, useFlashMessage } from '@indigo-ag/component-module';
import { AgFinancingDocumentType, AgFinancingFile, AgFinancingFileInternalStatus } from '@indigo-ag/schema';
import i18next from 'core/i18n';
import { compact, orderBy } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';

import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { getDocumentsQueryByFinancingId } from 'features/brazil-grower/queries';
import {
  ConfirmationDialog,
  SortType,
  TABLE_ACTIONS,
  TableSort,
  createStylesHook,
  documentRequestTypesOptions
} from 'shared';
import { UserUtil, downloadFilesAsZip, text } from 'shared/utils';
import { removeFilesFromAgFinancing } from '../BrazilGrowerFinance/utils/agFinancingFiles.util';
import { BrazilGrowerDocumentsList } from './BrazilGrowerDocumentsList/BrazilGrowerDocumentsList';
import { UploadDocumentModal } from './BrazilGrowerDocumentsList/UploadDocumentModal/UploadDocumentModal';

const useStyles = createStylesHook(() => ({
  updateDocumentLayout: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 24
  },
  downloadDocBtn: {
    marginLeft: 'auto !important',
    fontWeight: 500
  },
  uploadDocBtn: {
    marginLeft: 16,
    fontWeight: 500
  }
}));

export const defaultSort: TableSort = {
  id: 'documentType',
  direction: SortType.ASC
};

/**
 * Sorts a documents list based on the user's name that owns the document.
 *
 * Sorting by this field is not as straight forward, because we either take:
 * - Document's metadata `salesforceContentVersionModifiedByName` if available
 * - or the `uploadedBy` User name
 *
 * @param documents documents list to sort
 * @param sortDirection sort direction 'asc' | 'desc'
 * @returns documents list sorted by user name
 */
const sortDocumentsByUserName = (documents: AgFinancingFile[], sortDirection: SortType): AgFinancingFile[] => {
  const sortedDocuments: AgFinancingFile[] = [...documents];

  sortedDocuments.sort((a, b) => {
    const nameA = a.metadata?.salesforceContentVersionModifiedByName || UserUtil.getUserFullName(a.uploadedBy) || '';
    const nameB = b.metadata?.salesforceContentVersionModifiedByName || UserUtil.getUserFullName(b.uploadedBy) || '';

    return sortDirection === SortType.ASC ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
  });

  return sortedDocuments;
};

export const BrazilGrowerDocuments: React.FunctionComponent = () => {
  /**
   * Finance Process Context
   */
  const { agFinancingId } = useContext(FinanceProcessContext);

  /**
   * State
   */
  const [uploadDocumentModalOpen, setUploadDocumentModalOpen] = useState(false);
  const [sort, setSort] = useState<TableSort | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isBusy, setIsBusy] = useState(false);
  const [deleteFilesConfirmationModalOpen, setDeleteFilesConfirmationModalOpen] = useState(false);
  const [isDeletingFiles, setIsDeletingFiles] = useState(false);
  const [selectedDocTypes, setSelectedDocTypes] = useState<AgFinancingDocumentType[]>([]);
  const [sortedDocuments, setSortedDocuments] = useState<AgFinancingFile[]>([]);
  const [documentsQueryInfo, setDocumentsQueryInfo] = useState<PureQueryOptions>(
    getDocumentsQueryByFinancingId(agFinancingId, selectedDocTypes)
  );

  /**
   * Queries
   */
  const { data: docsData, loading: loadingDocs } = useQuery<{ financingAgFinancingFiles: AgFinancingFile[] }>(
    documentsQueryInfo.query,
    {
      variables: documentsQueryInfo.variables,
      fetchPolicy: 'cache-and-network' // To update the list with the documents just uploaded
    }
  );

  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * Effects
   */
  useEffect(() => {
    setDocumentsQueryInfo(getDocumentsQueryByFinancingId(agFinancingId, selectedDocTypes));
  }, [agFinancingId, selectedDocTypes]);

  useEffect(() => {
    let docsToSort = docsData ? [...docsData.financingAgFinancingFiles] : [];
    if (sort) {
      // * Manually group and sort documents
      const activeDocuments = docsToSort.filter(d => d.internalStatus === AgFinancingFileInternalStatus.ACTIVE);
      const oldDocuments = docsToSort.filter(d => d.internalStatus === AgFinancingFileInternalStatus.OLD);

      let sortedActiveDocuments: AgFinancingFile[] = [];
      let sortedOldDocuments: AgFinancingFile[] = [];

      // Sort by user's name is slightly different
      if (sort.id === 'uploadedBy.firstName') {
        sortedActiveDocuments = sortDocumentsByUserName(activeDocuments, sort.direction as SortType);
        sortedOldDocuments = sortDocumentsByUserName(oldDocuments, sort.direction as SortType);
      } else {
        sortedActiveDocuments = orderBy(activeDocuments, [sort.id], [sort.direction || SortType.DESC]);
        sortedOldDocuments = orderBy(oldDocuments, [sort.id], [sort.direction || SortType.DESC]);
      }

      setSortedDocuments([...sortedActiveDocuments, ...sortedOldDocuments]);
    } else {
      setSortedDocuments(docsToSort);
    }
  }, [docsData, sort]);

  const closeUploadDocumentModal = () => setUploadDocumentModalOpen(false);

  const { addFlashMessage } = useFlashMessage();

  const onTableAction = ({ type, payload }: any) => {
    switch (type) {
      case TABLE_ACTIONS.SORT_CHANGED:
        // Reset sorting after sort twice with on the same field
        if (sort?.id === payload.id && sort?.direction === SortType.ASC && payload.direction === SortType.ASC) {
          return setSort(null);
        }
        switch (payload.direction) {
          case null:
          case SortType.ASC:
            return setSort({
              direction: SortType.DESC,
              id: payload.id
            });
          case SortType.DESC:
            return setSort({
              ...defaultSort,
              id: payload.id
            });
        }
        break;
      case TABLE_ACTIONS.ROW_SELECTED:
        return setSelectedRows([...selectedRows, payload]);
      case TABLE_ACTIONS.ROW_DESELECTED:
        const newSelectedRows = compact(
          selectedRows.map((id, index) => {
            if (index === selectedRows.findIndex(id => id === payload)) {
              return null;
            }
            return id;
          })
        );
        return setSelectedRows(newSelectedRows);
      case TABLE_ACTIONS.ROW_SELECTION_CHANGED:
        const { values } = payload;
        return setSelectedRows(Array.from(values));
      case TABLE_ACTIONS.ALL_ROWS_DESELECTED:
        return setSelectedRows([]);
      default:
        break;
    }
  };

  const openDeleteFilesModal = () => {
    setDeleteFilesConfirmationModalOpen(true);
  };

  const closeDeleteFilesModal = () => {
    setDeleteFilesConfirmationModalOpen(false);
  };

  const deleteFiles = () => {
    setIsDeletingFiles(true);

    return removeFilesFromAgFinancing(selectedRows, [documentsQueryInfo])
      .then(() => {
        addFlashMessage({
          content: i18next.t('fileRemovedSuccessfully_plural', { count: selectedRows.length }),
          title: '',
          variant: 'success'
        });
        setSelectedRows([]);
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0]?.message || i18next.t('error'),
          title: '',
          variant: 'error'
        });
      })
      .finally(() => {
        setIsDeletingFiles(false);
      });
  };

  const onDownloadFiles = () => {
    setIsBusy(true);
    const selectedDocuments = sortedDocuments.filter(({ id }) => !!selectedRows.find(selectedId => selectedId === id));
    if (selectedDocuments) {
      const documents = selectedDocuments.map(({ documentUrl, documentName }) => {
        return {
          documentUrl,
          documentName
        };
      });
      downloadFilesAsZip(documents)
        .then(() => setSelectedRows([]))
        .catch((error: Error) =>
          addFlashMessage({
            content: error.message,
            title: '',
            variant: 'error'
          })
        )
        .finally(() => setIsBusy(false));
    }
  };

  const numberOfSelectedRows = selectedRows.length;

  return (
    <>
      <div className={classes.updateDocumentLayout}>
        <Multiselect
          filterable
          sizeVariant="slim"
          label={i18next.t('documentType')}
          name="documentType"
          options={documentRequestTypesOptions}
          value={selectedDocTypes}
          onSelectionChanged={setSelectedDocTypes}
          placeholder={i18next.t('pleaseSelect')}
          style={{ minWidth: 300 }}
        />
        <div className="flex">
          <Button
            className={classes.downloadDocBtn}
            variant="secondary"
            disabled={!numberOfSelectedRows}
            onClick={onDownloadFiles}
            busy={isBusy}
            faIcon={faDownload}
          >
            {text.firstLetterUppercase(i18next.t('download'))}{' '}
            {!!numberOfSelectedRows ? `(${numberOfSelectedRows})` : null}
          </Button>
          <Button
            className={classes.downloadDocBtn}
            variant="secondary"
            disabled={!numberOfSelectedRows}
            onClick={openDeleteFilesModal}
            busy={isDeletingFiles}
            faIcon={faTrash}
            destructive
          >
            {text.firstLetterUppercase(i18next.t('delete'))}{' '}
            {!!numberOfSelectedRows ? `(${numberOfSelectedRows})` : null}
          </Button>
          <LinkButton
            to={`/upload-documents/${agFinancingId}`}
            faIcon={faUpload}
            variant="secondary"
            className={classes.uploadDocBtn}
          >
            {i18next.t('uploadDocument')}
          </LinkButton>
        </div>
      </div>
      <UploadDocumentModal open={uploadDocumentModalOpen} closeModal={closeUploadDocumentModal} />
      <BrazilGrowerDocumentsList
        documents={sortedDocuments}
        onTableAction={onTableAction}
        sort={sort}
        selectedRows={selectedRows}
        clearSelectedRows={() => setSelectedRows([])}
        loading={loadingDocs}
      />
      <ConfirmationDialog
        open={deleteFilesConfirmationModalOpen}
        closeDialog={closeDeleteFilesModal}
        confirmationText={i18next.t('deleteFileConfirmation', { count: numberOfSelectedRows })}
        confirmationButtonText="delete"
        onConfirmPromise={deleteFiles}
        destructive
      />
    </>
  );
};
